import React, { useMemo } from "react";
import { Flex, Heading, Text, Icon, IconButton, Stack, Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { primary, white } from "../../../../../utils/colors";
import { TbAdjustmentsHorizontal } from "react-icons/tb";
import useThemeColors from "../../../../../hooks/useThemeColors";

const EmployeeEmploymentDetails = ({ employee, openEdit }) => {
  const { t } = useTranslation();

  const { whiteMode, backgroundLightMode, textPrimaryMode, textSecondaryMode, newBorderMode } =
    useThemeColors();

  const contractTypeLabel = useMemo(() => {
    if (!employee?.contractType) {
      return t("employee.contractNotSet");
    }

    const map = {
      permanent: t("employee.contractTypePermanent"),
      fixed_term: t("employee.contractTypeFixedTerm"),
      temporary: t("employee.contractTypeTemporary"),
    };

    return map[employee.contractType] || t("employee.contractNotSet");
  }, [employee?.contractType, t]);

  const compensationLabel = useMemo(() => {
    if (!employee?.compensationBasis) {
      return t("employee.contractNotSet");
    }

    const map = {
      fixed_salary: t("employee.compensationFixedSalary"),
      hourly: t("employee.compensationHourly"),
    };

    return map[employee.compensationBasis] || t("employee.contractNotSet");
  }, [employee?.compensationBasis, t]);

  const employmentLevelLabel = useMemo(() => {
    if (!employee?.employmentLevel) {
      return t("employee.contractNotSet");
    }

    const map = {
      full_time: t("employee.employmentLevelFullTime"),
      part_time: t("employee.employmentLevelPartTime"),
    };

    return map[employee.employmentLevel] || t("employee.contractNotSet");
  }, [employee?.employmentLevel, t]);

  const hasContractData = useMemo(() => {
    return (
      Boolean(employee?.contractType) ||
      Boolean(employee?.compensationBasis) ||
      Boolean(employee?.employmentLevel) ||
      Boolean(employee?.employmentLevelDetails) ||
      Boolean(employee?.commissionEnabled) ||
      Boolean(employee?.commissionDetails)
    );
  }, [
    employee?.commissionDetails,
    employee?.commissionEnabled,
    employee?.compensationBasis,
    employee?.contractType,
    employee?.employmentLevel,
    employee?.employmentLevelDetails,
  ]);

  const infoRows = useMemo(() => {
    const rows = [
      { label: t("employee.contractType"), value: contractTypeLabel },
      { label: t("employee.compensationBasis"), value: compensationLabel },
      { label: t("employee.employmentLevel"), value: employmentLevelLabel },
    ];

    if (employee?.employmentLevel === "part_time" && employee?.employmentLevelDetails) {
      rows.push({
        label: t("employee.employmentLevelDetails"),
        value: employee.employmentLevelDetails,
      });
    }

    rows.push({
      label: t("employee.commissionEnabled"),
      value: employee?.commissionEnabled ? t("common.yes") : t("common.no"),
    });

    if (employee?.commissionEnabled && employee?.commissionDetails) {
      rows.push({
        label: t("employee.commissionDetails"),
        value: employee.commissionDetails,
      });
    }

    return rows;
  }, [
    compensationLabel,
    contractTypeLabel,
    employee?.commissionDetails,
    employee?.commissionEnabled,
    employee?.employmentLevel,
    employee?.employmentLevelDetails,
    employmentLevelLabel,
    t,
  ]);

  return (
    <Flex direction="column" w="100%" p="0rem" position="relative">
      <Flex align="baseline" justify="space-between" m="0 0 0rem">
        <Heading fontSize="1.125rem" color={textPrimaryMode} fontWeight="600" m={0}>
          {t("employee.employmentDetails")}
        </Heading>
        <IconButton
          size="sm"
          fontSize="sm"
          bg={backgroundLightMode}
          border={`1px solid ${backgroundLightMode}`}
          color={textSecondaryMode}
          borderRadius="10rem"
          onClick={openEdit}
          _hover={{ bg: primary, color: white, border: primary }}
          aria-label={t("employee.editEmployee")}
        >
          <Icon as={TbAdjustmentsHorizontal} fontSize="0.875rem" />
        </IconButton>
      </Flex>

      <Flex
        w="100%"
        bg={whiteMode}
        direction="column"
        m="0.5rem 0 0"
        p="1.5rem"
        borderRadius="1rem"
        border={`1px solid ${newBorderMode}`}
        position="relative"
      >
        {hasContractData ? (
          <Stack spacing={3}>
            {infoRows.map((row) => (
              <Flex key={row.label} direction="column">
                <Text fontSize="sm" fontWeight="600" color={textSecondaryMode}>
                  {row.label}
                </Text>
                <Box mt="0.25rem">
                  <Text fontSize="md" fontWeight="500" color={textPrimaryMode} whiteSpace="pre-wrap">
                    {row.value || t("employee.contractNotSet")}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Stack>
        ) : (
          <Flex align="center" justify="center" minH="160px">
            <Text fontSize="sm" color={textSecondaryMode}>
              {t("employee.contractNotSet")}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default EmployeeEmploymentDetails;
